import type { ReconciliationAccount, Transaction } from '@/modules/projects'
import { cn, numberToCurrency } from '@/utils'
import { withShareLabel } from '../../reconciliation/helpers'
import { AddRow } from './AddRow'
import { InlineNumber } from './InlineNumber'
import { LedgerTable } from './LedgerTable'

export const AccountSection = ({
  account,
  rows,
  onEditAmount,
  onRemove,
  onRestore,
  onAdd,
  onEditAnchor,
  onJump,
}: {
  account: ReconciliationAccount
  rows: Transaction[]
  onEditAmount: (row: Transaction, next: number) => void
  onRemove: (row: Transaction) => void
  onRestore: (row: Transaction) => void
  onAdd: (input: { date: string; description: string; amount: number }) => void
  onEditAnchor: (side: 'begin' | 'end', next: number) => void
  onJump: (page: number | null, fileUrl: string) => void
}) => {
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-baseline gap-1.5">
          <span className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
            Account
          </span>
          <span className="text-sm font-medium tabular-nums text-foreground">
            {withShareLabel(
              account.last4 ? `••••${account.last4}` : 'Unknown',
              account.shareNumber,
            )}
          </span>
          {account.manuallyAdjusted && (
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-normal text-amber-700">
              edited
            </span>
          )}
        </span>
        <span
          className={cn(
            'text-xs font-medium tabular-nums',
            account.reconciled ? 'text-emerald-600' : 'text-amber-700',
          )}
        >
          {account.reconciled
            ? 'Balanced'
            : account.gap != null
              ? `off ${account.gap > 0 ? '+' : '-'}${numberToCurrency(Math.abs(account.gap))}`
              : 'Unverifiable'}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 border-y py-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Begin</span>
          <InlineNumber
            value={account.begin}
            onCommit={(next) => onEditAnchor('begin', next)}
            className="font-medium text-foreground"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Net</span>
          <span className="tabular-nums text-foreground">
            {account.sum == null ? '--' : numberToCurrency(account.sum)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">End</span>
          <InlineNumber
            value={account.end}
            onCommit={(next) => onEditAnchor('end', next)}
            className="font-medium text-foreground"
          />
        </div>
      </div>

      <AddRow onAdd={onAdd} />

      <LedgerTable
        rows={rows}
        onEditAmount={onEditAmount}
        onRemove={onRemove}
        onRestore={onRestore}
        onJump={onJump}
      />
    </div>
  )
}
